import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem, Product, CustomerDetails, Order, ProductVariant } from '../types';
import { useSettingsStore } from './settingsStore';
import { useOrderStore } from './orderStore';
import { useProductStore } from './productStore';
import { sendRealEmail } from '../services/emailService';
import { formatPriceRaw } from '../utils/price';

interface CartState {
  items: CartItem[];
  wishlist: Product[];
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  
  // Cart Actions
  addItem: (product: Product, quantity?: number, selectedOptions?: Record<string, string>, selectedVariant?: ProductVariant) => void;
  removeItem: (cartItemId: string) => void;
  updateQuantity: (cartItemId: string, quantity: number) => void;
  clearCart: () => void;
  
  // Wishlist Actions
  toggleWishlist: (product: Product) => void;
  isInWishlist: (productId: string) => boolean;

  // Checkout Action
  checkout: (customerDetails: CustomerDetails, paymentMethod: Order['paymentMethod'], userId?: string) => Promise<Order>;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      wishlist: [],
      isCartOpen: false,
      setIsCartOpen: (open) => set({ isCartOpen: open }),

      addItem: (product, quantity = 1, selectedOptions, selectedVariant) => set((state) => {
        const variantId = selectedVariant?.id;
        const cartItemId = product.id + (variantId ? `-${variantId}` : '');
        const maxStock = selectedVariant ? selectedVariant.stock : product.stock;

        const existingItem = state.items.find((item) => item.id === cartItemId);
        const newItems = existingItem
          ? state.items.map((item) => 
              item.id === cartItemId
                ? { ...item, quantity: Math.min(item.quantity + quantity, maxStock) }
                : item
            )
          : [...state.items, { 
              id: cartItemId, 
              product, 
              quantity: Math.min(quantity, maxStock), 
              selectedOptions, 
              selectedVariant 
            }];
        return {
          items: newItems
        };
      }),

      removeItem: (cartItemId) => set((state) => ({
        items: state.items.filter((item) => item.id !== cartItemId)
      })),

      updateQuantity: (cartItemId, quantity) => set((state) => ({
        items: state.items.map((item) => {
          if (item.id === cartItemId) {
            const maxStock = item.selectedVariant ? item.selectedVariant.stock : item.product.stock;
            return { ...item, quantity: Math.max(1, Math.min(quantity, maxStock)) };
          }
          return item;
        })
      })),

      clearCart: () => set({ items: [] }),

      toggleWishlist: (product) => set((state) => {
        const exists = state.wishlist.some((p) => p.id === product.id);
        if (exists) {
          return { wishlist: state.wishlist.filter((p) => p.id !== product.id) };
        } else {
          return { wishlist: [...state.wishlist, product] };
        }
      }),

      isInWishlist: (productId) => {
        return get().wishlist.some((p) => p.id === productId);
      },

      checkout: async (customerDetails, paymentMethod, userId) => {
        const { items } = get();
        const settings = useSettingsStore.getState().deliverySettings || { freeThreshold: 4990, baseFee: 350 };
        const freeThreshold = typeof settings.freeThreshold === 'number' ? settings.freeThreshold : 4990;
        const baseFee = typeof settings.baseFee === 'number' ? settings.baseFee : 350;
        const codBaseFee = typeof settings.codBaseFee === 'number' ? settings.codBaseFee : baseFee;
        
        // Calculate totals
        const subtotal = items.reduce((acc, item) => {
          const itemPrice = item.selectedVariant ? item.selectedVariant.price : item.product.price;
          return acc + itemPrice * item.quantity;
        }, 0);
        const activeBaseFee = paymentMethod === 'Cash on Delivery' ? codBaseFee : baseFee;
        const deliveryCharge = subtotal >= freeThreshold ? 0 : activeBaseFee;
        const total = subtotal + deliveryCharge;
        
        const orderId = 'SZ-' + Math.floor(1000 + Math.random() * 9000);
        
        const newOrder: Order = {
          id: orderId,
          userId,
          customerDetails,
          items: [...items],
          subtotal,
          deliveryCharge,
          total,
          paymentMethod,
          status: 'Pending',
          createdAt: new Date().toISOString()
        };

        try {
          // Add order to order store
          await useOrderStore.getState().addOrder(newOrder);

          // Send simulated order confirmation email if customer has email
          if (customerDetails.email) {
            try {
              const templates = useSettingsStore.getState().emailTemplates;
              const template = paymentMethod === 'Cash on Delivery' 
                ? (templates?.orderConfirmationCOD || templates?.orderConfirmation)
                : templates?.orderConfirmation;
              const whatsappNumber = useSettingsStore.getState().whatsappNumber;
              
              if (template) {
                const formattedItemsList = items.map(item => {
                  const itemPrice = item.selectedVariant ? item.selectedVariant.price : item.product.price;
                  const optionsText = item.selectedOptions
                    ? ' (' + Object.entries(item.selectedOptions).map(([k, v]) => `${k}: ${v}`).join(', ') + ')'
                    : '';
                  return `- ${item.product.name}${optionsText} (Qty: ${item.quantity}) = LKR ${formatPriceRaw(itemPrice * item.quantity)}`;
                }).join('\n') + `\n\nSubtotal: LKR ${formatPriceRaw(subtotal)}\nDelivery: ${deliveryCharge === 0 ? 'FREE' : `LKR ${formatPriceRaw(deliveryCharge)}`}\nGrand Total: LKR ${formatPriceRaw(total)}`;
                  
                const subject = template.subject
                  .replace(/{{orderId}}/g, orderId)
                  .replace(/{{customerName}}/g, customerDetails.name)
                  .replace(/{{total}}/g, total.toLocaleString())
                  .replace(/{{itemsList}}/g, 'Order Items');
                  
                const hasItemsList = template.body.includes('{{itemsList}}');
                let bodyText = template.body
                  .replace(/{{orderId}}/g, orderId)
                  .replace(/{{customerName}}/g, customerDetails.name)
                  .replace(/{{total}}/g, total.toLocaleString())
                  .replace(/{{whatsappNumber}}/g, whatsappNumber)
                  .replace(/{{deliveryAddress}}/g, `${customerDetails.address}, ${customerDetails.city}`);
                  
                if (hasItemsList) {
                  bodyText = bodyText.replace(/{{itemsList}}/g, formattedItemsList);
                }
                
                const plainTextBody = `${bodyText}
 
${!hasItemsList ? `Order Details:\n${formattedItemsList}` : ''}`.trim();
                  
                const simulatedEmail = {
                  id: 'EML-' + Math.floor(100000 + Math.random() * 900000),
                  orderId,
                  to: customerDetails.email,
                  subject,
                  body: plainTextBody,
                  sentAt: new Date().toISOString(),
                  type: 'order_confirmation' as const
                };
                
                await useOrderStore.getState().addEmail(simulatedEmail);

                // Dispatch real email in background
                sendRealEmail({
                  to_name: customerDetails.name,
                  to_email: customerDetails.email,
                  from_name: 'SensorZone.lk',
                  subject,
                  message: plainTextBody,
                  reply_to: 'sensorzonetronic@gmail.com',
                  order_id: orderId,
                  total: formatPriceRaw(total),
                  whatsapp_number: whatsappNumber,
                  items_list: formattedItemsList // Optional, mostly just included in htmlBody now
                }).catch(err => console.error('Failed to send real email:', err));
              }
            } catch (error) {
              console.error('Error handling order email:', error);
            }
          }

          // Deduct stock in product store
          items.forEach((item) => {
            const currentProd = useProductStore.getState().products.find(p => p.id === item.product.id);
            if (currentProd) {
              useProductStore.getState().deductProductStock(item.product.id, item.quantity, item.selectedVariant?.id);
            }
          });

          // Clear cart
          get().clearCart();
        } catch (error) {
          console.error('Checkout error:', error);
        }

        return newOrder;
      }
    }),
    {
      name: 'sensorzone-cart',
      partialize: (state) => ({
        items: state.items,
        wishlist: state.wishlist,
      }),
    }
  )
);
